import React, { useState, useEffect, useRef } from 'react';
import Badge from 'react-bootstrap/Badge';
import ReactJson from 'react-json-view';
import { getDeviceCommands } from '../../../../libs/dashboard';
import { useAuth } from '../../../../../../contexts/AuthContext';
import config from '../../../../../../config';

// ── Signal helpers (same logic as RemoteDetectionEvent) ─────────────────────

function hexToBits(hex) {
    const bits = new Uint8Array(hex.length * 4);
    for (let i = 0; i < hex.length; i++) {
        const n = parseInt(hex[i], 16);
        if (isNaN(n)) continue;
        bits[i * 4]     = (n >> 3) & 1;
        bits[i * 4 + 1] = (n >> 2) & 1;
        bits[i * 4 + 2] = (n >> 1) & 1;
        bits[i * 4 + 3] =  n       & 1;
    }
    return bits;
}

function SignalCanvas({ raw }) {
    const canvasRef    = useRef(null);
    const [zoom, setZoom]       = useState(1);
    const [panFrac, setPanFrac] = useState(0);
    const isDragging   = useRef(false);
    const dragStartX   = useRef(0);
    const dragStartPan = useRef(0);

    const clampPan = (p, z) => Math.max(0, Math.min(1 - 1 / z, p));

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const cursorFrac = (e.clientX - rect.left) / rect.width;
            const factor = e.deltaY < 0 ? 1.5 : 1 / 1.5;
            setZoom(prevZoom => {
                const newZoom = Math.max(1, Math.min(128, prevZoom * factor));
                const bitFrac = dragStartPan.current + cursorFrac / prevZoom;
                setPanFrac(prev => clampPan(bitFrac - cursorFrac / newZoom, newZoom));
                return newZoom;
            });
        };
        canvas.addEventListener('wheel', onWheel, { passive: false });
        return () => canvas.removeEventListener('wheel', onWheel);
    }, []);

    useEffect(() => { dragStartPan.current = panFrac; }, [panFrac]);

    useEffect(() => {
        if (!raw || !canvasRef.current) return;
        const allBits = hexToBits(raw);
        const canvas  = canvasRef.current;
        const ctx     = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;
        const topY = Math.round(H * 0.10);
        const botY = Math.round(H * 0.90);

        const winStart  = Math.floor(panFrac * allBits.length);
        const winEnd    = Math.min(allBits.length, Math.ceil((panFrac + 1 / zoom) * allBits.length));
        const bits      = allBits.subarray(winStart, winEnd);
        const bitsPerCol = bits.length / W;

        ctx.fillStyle = '#0d1117';
        ctx.fillRect(0, 0, W, H);

        ctx.strokeStyle = '#1c2128';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, H / 2);
        ctx.lineTo(W, H / 2);
        ctx.stroke();

        const levels = new Uint8Array(W);
        for (let x = 0; x < W; x++) {
            const s = Math.floor(x * bitsPerCol);
            const e = Math.ceil((x + 1) * bitsPerCol);
            const count = Math.max(1, e - s);
            let ones = 0;
            for (let b = s; b < e && b < bits.length; b++) ones += bits[b];
            levels[x] = (ones / count) >= 0.5 ? 1 : 0;
        }

        ctx.fillStyle = 'rgba(88,166,255,0.10)';
        let fillStart = null;
        for (let x = 0; x <= W; x++) {
            const hi = x < W ? levels[x] === 1 : false;
            if (hi && fillStart === null) { fillStart = x; }
            else if (!hi && fillStart !== null) {
                ctx.fillRect(fillStart, topY, x - fillStart, botY - topY);
                fillStart = null;
            }
        }

        ctx.strokeStyle = '#58a6ff';
        ctx.lineWidth = 1.5;
        ctx.lineJoin = 'miter';
        ctx.beginPath();
        let prevLvl = levels[0];
        ctx.moveTo(0, prevLvl ? topY : botY);
        for (let x = 1; x < W; x++) {
            const lvl = levels[x];
            if (lvl !== prevLvl) {
                ctx.lineTo(x, prevLvl ? topY : botY);
                ctx.lineTo(x, lvl    ? topY : botY);
                prevLvl = lvl;
            } else {
                ctx.lineTo(x, lvl ? topY : botY);
            }
        }
        ctx.stroke();

        if (zoom > 1) {
            const sbH = 3, sbY = H - sbH - 1;
            ctx.fillStyle = 'rgba(255,255,255,0.08)';
            ctx.fillRect(0, sbY, W, sbH);
            ctx.fillStyle = '#58a6ff';
            ctx.fillRect(Math.round(panFrac * W), sbY, Math.max(4, Math.round(W / zoom)), sbH);
        }
    }, [raw, zoom, panFrac]);

    const handleMouseDown = (e) => {
        isDragging.current  = true;
        dragStartX.current  = e.clientX;
        dragStartPan.current = panFrac;
        e.currentTarget.style.cursor = 'grabbing';
    };
    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        setPanFrac(clampPan(dragStartPan.current - (e.clientX - dragStartX.current) / rect.width / zoom, zoom));
    };
    const stopDrag = (e) => {
        isDragging.current = false;
        if (e.currentTarget) e.currentTarget.style.cursor = zoom > 1 ? 'grab' : 'default';
    };

    return (
        <canvas
            ref={canvasRef}
            width={900}
            height={80}
            style={{ width: '100%', height: '80px', borderRadius: '4px', display: 'block', cursor: zoom > 1 ? 'grab' : 'default' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
            onDoubleClick={() => { setZoom(1); setPanFrac(0); }}
        />
    );
}

// ── Frequency formatter ──────────────────────────────────────────────────────

function formatFreq(hz) {
    if (!hz) return null;
    return hz >= 1_000_000
        ? (hz / 1_000_000).toFixed(3) + ' MHz'
        : (hz / 1000).toFixed(2) + ' kHz';
}

// ── Single command row ───────────────────────────────────────────────────────

function CommandItem({ cmd, bondId, deviceId }) {
    const { getToken } = useAuth();
    const [showSignal,  setShowSignal]  = useState(false);
    const [showJson,    setShowJson]    = useState(false);
    const [liveSignal,  setLiveSignal]  = useState(null);
    const [reloading,   setReloading]   = useState(false);

    const data      = cmd.data     || {};
    const sig       = liveSignal   || cmd.signal || {};
    const signalHex = sig.data;
    const freqLabel = formatFreq(sig.freq);

    const handleReloadSignal = async () => {
        const commandId = cmd.command_id;
        if (!bondId || !deviceId || !commandId) return;
        setReloading(true);
        try {
            const token = await getToken();
            const url = `${config.api.palantirApi}${bondId}/devices/${deviceId}/commands/${commandId}/signal/`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            const b = json?.b;
            if (b && b.data) {
                setLiveSignal({ data: b.data, freq: b.freq, bps: b.bps, modulation: b.modulation });
                setShowSignal(true);
            }
        } catch (err) {
            console.error('Failed to reload signal:', err);
        } finally {
            setReloading(false);
        }
    };

    return (
        <div
            className="mb-1 px-2 py-1 rounded"
            style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
        >
            {/* Title row: button_type · name · freq  +  toggles flush right */}
            <div className="d-flex align-items-center flex-wrap gap-1">
                {data.button_type && (
                    <Badge bg="secondary" style={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
                        {data.button_type}
                    </Badge>
                )}
                {data.name && (
                    <Badge style={{ background: '#1f6feb', fontSize: '0.7rem' }}>
                        {data.name}
                    </Badge>
                )}
                {freqLabel && (
                    <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                        {freqLabel}
                    </Badge>
                )}
                {data.hidden && (
                    <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem' }}>hidden</Badge>
                )}

                {/* Right-side toggles */}
                <div className="ms-auto d-flex gap-1">
                    {signalHex && (
                        <Badge
                            bg={showSignal ? 'secondary' : 'dark'}
                            style={{ cursor: 'pointer', fontSize: '0.7rem', userSelect: 'none',
                                     border: '1px solid rgba(255,255,255,0.15)', padding: '0.25em 0.5em' }}
                            onClick={() => setShowSignal(s => !s)}
                            title={showSignal ? 'Hide signal' : 'Show signal'}
                        >
                            {showSignal ? '▲' : '▼'} Signal
                        </Badge>
                    )}
                    {bondId && deviceId && cmd.command_id && (
                        <Badge
                            bg="dark"
                            style={{ cursor: reloading ? 'not-allowed' : 'pointer', fontSize: '0.7rem',
                                     userSelect: 'none', border: '1px solid rgba(255,255,255,0.15)',
                                     padding: '0.25em 0.5em', opacity: reloading ? 0.6 : 1 }}
                            onClick={reloading ? undefined : handleReloadSignal}
                            title="Reload signal from Bond"
                        >
                            <span style={{ fontSize: '1rem', lineHeight: 1 }}>{reloading ? '⟳' : '↺'}</span>
                        </Badge>
                    )}
                    <Badge
                        bg={showJson ? 'secondary' : 'dark'}
                        style={{ cursor: 'pointer', fontSize: '0.7rem', userSelect: 'none',
                                 border: '1px solid rgba(255,255,255,0.15)', padding: '0.25em 0.5em' }}
                        onClick={() => setShowJson(s => !s)}
                        title="Toggle JSON"
                    >
                        {'{}'}
                    </Badge>
                </div>
            </div>

            {/* Signal waveform */}
            {showSignal && signalHex && (
                <div style={{ marginTop: '6px' }}>
                    <div style={{ background: '#0d1117', borderRadius: '6px', padding: '4px 6px 6px' }}>
                        <div style={{ color: '#6e7681', fontSize: '10px', marginBottom: '2px', fontFamily: 'monospace' }}>
                            RF signal — {(signalHex.length * 4).toLocaleString()} bits
                            {freqLabel && ` · ${freqLabel}`}
                            {sig.modulation && ` · ${sig.modulation}`}
                            {sig.bps && ` · ${(sig.bps / 1000).toFixed(0)} kbps`}
                        </div>
                        <SignalCanvas raw={signalHex} />
                    </div>
                </div>
            )}

            {/* Raw JSON */}
            {showJson && (
                <div style={{ marginTop: '6px', overflowX: 'auto' }}>
                    <ReactJson
                        src={{ data, signal: sig }}
                        theme="ocean"
                        collapsed={1}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={true}
                        name={false}
                    />
                </div>
            )}
        </div>
    );
}

// ── Commands list (fetches on mount) ─────────────────────────────────────────

function DeviceCommandsList({ bondId, deviceId }) {
    const { getToken } = useAuth();
    const [commands, setCommands] = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getDeviceCommands(getToken(), bondId, deviceId)
            .then(data  => { if (!cancelled) setCommands(Array.isArray(data) ? data : []); })
            .catch(err  => { if (!cancelled) setError(err.message); })
            .finally(()  => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [bondId, deviceId]);

    if (loading)  return <div className="text-muted small p-2">Loading commands…</div>;
    if (error)    return <div className="text-danger small p-2">Error: {error}</div>;
    if (!commands || commands.length === 0)
        return <div className="text-muted small p-2">No commands found.</div>;

    // Group by category_name
    const byCategory = {};
    commands.forEach(cmd => {
        const cat = cmd.data?.category_name || 'Other';
        if (!byCategory[cat]) byCategory[cat] = [];
        byCategory[cat].push(cmd);
    });

    return (
        <div>
            {Object.entries(byCategory).map(([cat, cmds]) => (
                <div key={cat} className="mb-3">
                    <div
                        className="text-muted mb-1"
                        style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
                    >
                        {cat}
                    </div>
                    {cmds.map(cmd => (
                        <CommandItem key={cmd.id || cmd.command_id} cmd={cmd} bondId={bondId} deviceId={deviceId} />
                    ))}
                </div>
            ))}
        </div>
    );
}

export default DeviceCommandsList;
